import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, ShareIcon, MapPinIcon, DollarSignIcon, CalendarIcon, ClockIcon, BuildingIcon, CheckCircleIcon } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
const JobDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  // Mock job data - in a real app, you would fetch this based on the ID
  const job = {
    id,
    title: 'Senior React Developer',
    company: 'SpaceX',
    logo: 'https://logo.clearbit.com/spacex.com',
    location: 'Remote',
    salary: '$120k - $150k',
    tags: ['Remote', 'Full-time'],
    posted: '2 days ago',
    description: `
      We're looking for a Senior React Developer to join our growing team at SpaceX. 
      You'll be responsible for building and maintaining user interfaces for our mission control systems.
      This is a unique opportunity to work on cutting-edge technology that is literally helping humanity become multi-planetary.
    `,
    responsibilities: ['Design, develop and maintain complex React applications', 'Work closely with backend engineers, designers, and product managers', 'Optimize applications for maximum speed and scalability', 'Write clean, maintainable, and well-documented code', 'Participate in code reviews and mentor junior developers'],
    requirements: ['5+ years of experience with React and modern JavaScript', 'Strong understanding of state management (Redux, Context API)', 'Experience with responsive design and CSS-in-JS libraries', 'Knowledge of modern front-end build pipelines and tools', 'Excellent problem-solving skills and attention to detail'],
    benefits: ['Competitive salary and equity package', 'Health, dental, and vision insurance', 'Flexible work schedule', '401(k) matching', 'Paid time off and parental leave', 'Professional development budget'],
    applicationDeadline: '2023-12-15',
    employmentType: 'Full-time',
    experienceLevel: 'Senior'
  };
  const handleBack = () => {
    navigate(-1);
  };
  const handleApply = () => {
    navigate(`/apply/${id}`);
  };
  return <div className="pb-20" data-id="element-173">
      <div className="bg-gradient-to-r from-teal-500 to-cyan-400 p-4 flex items-center" data-id="element-174">
        <button onClick={handleBack} className="p-2 rounded-full bg-white/20 text-white mr-4" data-id="element-175">
          <ArrowLeftIcon size={20} data-id="element-176" />
        </button>
        <h1 className="text-lg font-bold text-white" data-id="element-177">Job Details</h1>
      </div>
      <div className="p-4" data-id="element-178">
        <Card className="mb-6" data-id="element-179">
          <div className="flex items-start justify-between" data-id="element-180">
            <div className="flex items-start gap-4" data-id="element-181">
              <div className="h-16 w-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-182">
                {job.logo ? <img src={job.logo} alt={job.company} className="h-full w-full object-cover" data-id="element-183" /> : <BuildingIcon className="text-gray-400" size={24} data-id="element-184" />}
              </div>
              <div data-id="element-185">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100" data-id="element-186">
                  {job.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400" data-id="element-187">
                  {job.company}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-188">
                  <div className="flex items-center" data-id="element-189">
                    <MapPinIcon size={14} className="mr-1" data-id="element-190" />
                    {job.location}
                  </div>
                  <div className="flex items-center" data-id="element-191">
                    <DollarSignIcon size={14} className="mr-1" data-id="element-192" />
                    {job.salary}
                  </div>
                  <div className="flex items-center" data-id="element-193">
                    <CalendarIcon size={14} className="mr-1" data-id="element-194" />
                    {job.posted}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex" data-id="element-195">
              <button className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 mr-2" data-id="element-196">
                <BookmarkIcon size={20} data-id="element-197" />
              </button>
              <button className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-198">
                <ShareIcon size={20} data-id="element-199" />
              </button>
            </div>
          </div>
          <div className="mt-4 flex gap-2" data-id="element-200">
            {job.tags.map((tag: string, index: number) => <span key={index} className={`text-xs px-2 py-1 rounded-full ${tag === 'Remote' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : tag === 'Hybrid' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`} data-id="element-201">
                {tag}
              </span>)}
          </div>
          <div className="mt-6 flex justify-between items-center" data-id="element-202">
            <div className="flex items-center" data-id="element-203">
              <ClockIcon size={16} className="mr-1 text-gray-500 dark:text-gray-400" data-id="element-204" />
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-205">
                Application deadline: {job.applicationDeadline}
              </span>
            </div>
            <Button variant="primary" onClick={handleApply} data-id="element-206">
              Apply Now
            </Button>
          </div>
        </Card>
        <Card className="mb-6" data-id="element-207">
          <h3 className="text-lg font-semibold mb-3" data-id="element-208">Job Description</h3>
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line" data-id="element-209">
            {job.description}
          </p>
          <h3 className="text-lg font-semibold mt-6 mb-3" data-id="element-210">Responsibilities</h3>
          <ul className="space-y-2" data-id="element-211">
            {job.responsibilities.map((item, index) => <li key={index} className="flex items-start" data-id="element-212">
                <CheckCircleIcon size={18} className="mr-2 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-213" />
                <span className="text-gray-700 dark:text-gray-300" data-id="element-214">{item}</span>
              </li>)}
          </ul>
          <h3 className="text-lg font-semibold mt-6 mb-3" data-id="element-215">Requirements</h3>
          <ul className="space-y-2" data-id="element-216">
            {job.requirements.map((item, index) => <li key={index} className="flex items-start" data-id="element-217">
                <CheckCircleIcon size={18} className="mr-2 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-218" />
                <span className="text-gray-700 dark:text-gray-300" data-id="element-219">{item}</span>
              </li>)}
          </ul>
          <h3 className="text-lg font-semibold mt-6 mb-3" data-id="element-220">Benefits</h3>
          <ul className="space-y-2" data-id="element-221">
            {job.benefits.map((item, index) => <li key={index} className="flex items-start" data-id="element-222">
                <CheckCircleIcon size={18} className="mr-2 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-223" />
                <span className="text-gray-700 dark:text-gray-300" data-id="element-224">{item}</span>
              </li>)}
          </ul>
        </Card>
        <Card className="mb-6" data-id="element-225">
          <h3 className="text-lg font-semibold mb-3" data-id="element-226">About {job.company}</h3>
          <p className="text-gray-700 dark:text-gray-300" data-id="element-227">
            SpaceX designs, manufactures and launches advanced rockets and
            spacecraft. The company was founded in 2002 to revolutionize space
            technology, with the ultimate goal of enabling people to live on
            other planets.
          </p>
          <div className="mt-4" data-id="element-228">
            <a href="#" className="text-teal-500 dark:text-teal-400 hover:underline" data-id="element-229">
              Visit company website
            </a>
          </div>
        </Card>
        <Card className="mb-6" data-id="element-230">
          <h3 className="text-lg font-semibold mb-3" data-id="element-231">Similar Jobs</h3>
          <div className="space-y-4" data-id="element-232">
            <div className="flex items-start gap-3 pb-4 border-b border-gray-200 dark:border-gray-700" data-id="element-233">
              <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-234">
                <img src="https://logo.clearbit.com/google.com" alt="Google" className="h-full w-full object-cover" data-id="element-235" />
              </div>
              <div data-id="element-236">
                <h4 className="font-medium text-gray-900 dark:text-gray-100" data-id="element-237">
                  React Developer
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-238">
                  Google • Remote
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-239">
                  $110k - $140k
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-240">
              <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-241">
                <img src="https://logo.clearbit.com/microsoft.com" alt="Microsoft" className="h-full w-full object-cover" data-id="element-242" />
              </div>
              <div data-id="element-243">
                <h4 className="font-medium text-gray-900 dark:text-gray-100" data-id="element-244">
                  Frontend Engineer
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-245">
                  Microsoft • Hybrid
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-246">
                  $115k - $145k
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className="fixed bottom-20 left-0 right-0 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700" data-id="element-247">
        <Button variant="primary" fullWidth size="lg" onClick={handleApply} data-id="element-248">
          Apply for this Job
        </Button>
      </div>
    </div>;
};
export default JobDetail;